import { IconName, MinimalIconProps } from '@ardoq/icons';
import { WithPopover, PopoverContentType } from '@ardoq/popovers';
import { StatusType, Tag } from '@ardoq/status-ui';
import { WidgetLinkedDashboardFilter } from '../types';
import { isEmpty } from 'lodash';
import { InlineText, Text } from '@ardoq/typography';
import { FlexBox } from '@ardoq/layout';
import { type I18nLocale, t } from '@ardoq/i18n';

type ActiveDashboardFiltersIndicatorProps = {
  popoverContent: PopoverContentType;
  statusType: StatusType;
  rightIcon: MinimalIconProps | undefined;
  label: string | undefined;
};

export const getActiveDashboardFiltersIndicatorProps = (
  activeLinkedDashboardFilters: WidgetLinkedDashboardFilter[],
  i18nLocale: I18nLocale
): ActiveDashboardFiltersIndicatorProps => {
  const successfulDashboardFilters = activeLinkedDashboardFilters.filter(
    filter => !filter.warning
  );
  const failedDashboardFilters = activeLinkedDashboardFilters.filter(
    filter => filter.warning
  );
  const isAllFiltersFailed = isEmpty(successfulDashboardFilters);
  const isFiltersPartiallyFailed =
    !isAllFiltersFailed && !isEmpty(failedDashboardFilters);

  const successfulNames = successfulDashboardFilters
    .map(param => param.name)
    .join(', ');
  const failedNames = failedDashboardFilters
    .map(filter => filter.name)
    .join(', ');

  const popoverContentRenderer = () => (
    <FlexBox flexDirection="column" gap="medium">
      {!isAllFiltersFailed && (
        <Text>
          <InlineText variant="text2Bold">
            {successfulDashboardFilters.length === 1
              ? t(
                  i18nLocale,
                  'dashboard:filtersIndicator.appliedFilter',
                  'Applied dashboard filter'
                )
              : t(
                  i18nLocale,
                  'dashboard:filtersIndicator.appliedFilters',
                  'Applied dashboard filters'
                )}
          </InlineText>
          : {successfulNames}
        </Text>
      )}
      {(isAllFiltersFailed || isFiltersPartiallyFailed) && (
        <Text>
          {failedDashboardFilters.length === 1
            ? t(
                i18nLocale,
                'dashboard:filtersIndicator.cantBeAppliedSingle',
                { names: failedNames },
                '{{names}} dashboard filter can’t be applied to this chart'
              )
            : t(
                i18nLocale,
                'dashboard:filtersIndicator.cantBeAppliedPlural',
                { names: failedNames },
                '{{names}} dashboard filters can’t be applied to this chart'
              )}
        </Text>
      )}
    </FlexBox>
  );

  return {
    popoverContent: popoverContentRenderer,
    statusType: isAllFiltersFailed
      ? StatusType.WARNING
      : isFiltersPartiallyFailed
        ? StatusType.DEFAULT
        : StatusType.INFO,
    rightIcon:
      isAllFiltersFailed || isFiltersPartiallyFailed
        ? { name: IconName.WARNING, color: 'orange' }
        : undefined,
    label: successfulDashboardFilters.length
      ? `${successfulDashboardFilters.length}`
      : undefined,
  };
};

export const ActiveDashboardFiltersIndicator = ({
  popoverContent,
  statusType,
  rightIcon,
  label,
}: ActiveDashboardFiltersIndicatorProps) => (
  <WithPopover content={popoverContent}>
    <Tag
      statusType={statusType}
      leftIcon={{ name: IconName.FILTER_LIST }}
      rightIcon={rightIcon}
      label={label}
      style={{
        display: 'flex',
        cursor: 'default',
      }}
    />
  </WithPopover>
);
